import React, { useCallback, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Alert, Snackbar, Stack } from '@mui/material';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';

import { GET_SITE_PAS_FINDS_QUERY, reconcileFindsInCache } from '../../../gql';
import { TabBar, TabBarItem } from '../../../components/design-system';
import {
  resetFindResultsPages,
  useFindResultsState,
} from '../../../components/FindResults';
import {
  AmcrPasCreateDialog,
  AmcrPasFile,
  AmcrPasPhotoDialog,
  AmcrPasSubmitDialog,
} from '../../../components/AmcrPasDialog';
import {
  FindPasEditDialog,
  PasFind,
} from '../../../components/FindPasEditDialog';
import { PasFindTable } from './PasFindTable';
import {
  PAS_STAGES,
  PAS_STAGE_PATHS,
  PasStage,
  getPasStage,
  pasStageMessages,
} from './pasStages';

const NO_FINDS: PasFind[] = [];
const TOAST_DURATION_MS = 5000;

interface SitePasProps {
  pasEnabled: boolean;
}

const NO_PAS_FILES: AmcrPasFile[] = [];

const sitePasMessages = defineMessages({
  stages: { id: 'sitePas.stages', defaultMessage: 'PAS stages' },
  saved: { id: 'findPasEdit.success', defaultMessage: 'The find was saved.' },
  created: {
    id: 'amcrPas.create.success',
    defaultMessage: 'The find was created in PAS as {amcrId}.',
  },
  photosAdded: {
    id: 'amcrPas.photos.success',
    defaultMessage:
      '{count, plural, one {# photo was} other {# photos were}} added to PAS.',
  },
  submitted: {
    id: 'sitePas.submit.success',
    defaultMessage: 'The find was submitted for approval.',
  },
});

const getStagePathname = (siteId: string, stage: PasStage) => {
  const stagePath = PAS_STAGE_PATHS[stage];

  return stagePath ? `/s/${siteId}/pas/${stagePath}` : `/s/${siteId}/pas`;
};

const groupByStage = (finds: PasFind[]) => {
  const groups = Object.fromEntries(
    PAS_STAGES.map((stage) => [stage, [] as PasFind[]])
  ) as Record<PasStage, PasFind[]>;

  finds.forEach((find) => {
    groups[getPasStage(find)].push(find);
  });

  return groups;
};

const SitePas = React.memo(({ pasEnabled }: SitePasProps) => {
  const intl = useIntl();
  const client = useApolloClient();
  const location = useLocation();
  const navigate = useNavigate();
  const { siteId = '', stage: stagePath = '' } = useParams();
  const state = useFindResultsState();
  const [editedFind, setEditedFind] = useState<PasFind | null>(null);
  const [createFindId, setCreateFindId] = useState<string | null>(null);
  const [photosFind, setPhotosFind] = useState<PasFind | null>(null);
  const [submitFindId, setSubmitFindId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const stage = PAS_STAGES.find(
    (candidate) => PAS_STAGE_PATHS[candidate] === stagePath
  );

  const { data, loading } = useQuery(GET_SITE_PAS_FINDS_QUERY, {
    variables: { siteId, filters: state.filter },
    fetchPolicy: 'cache-and-network',
    skip: !siteId,
  });
  const finds: PasFind[] = data?.getSiteFinds ?? NO_FINDS;
  const findsByStage = useMemo(() => groupByStage(finds), [finds]);

  const tabs = useMemo<TabBarItem[]>(
    () =>
      PAS_STAGES.map((value) => ({
        value,
        label: intl.formatMessage(pasStageMessages[value]),
        count: findsByStage[value].length,
      })),
    [findsByStage, intl]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const params = new URLSearchParams(location.search);

      resetFindResultsPages(params);
      navigate({
        pathname: getStagePathname(siteId, value as PasStage),
        search: params.toString(),
      });
    },
    [location.search, navigate, siteId]
  );

  const handleEdit = useCallback((find: PasFind) => {
    setEditedFind(find);
  }, []);

  const handleCreate = useCallback((findId: string) => {
    setCreateFindId(findId);
  }, []);

  const handleAddPhotos = useCallback((find: PasFind) => {
    setPhotosFind(find);
  }, []);

  const handleSubmit = useCallback((find: PasFind) => {
    setSubmitFindId(find.id);
  }, []);

  const handleEditClose = () => {
    setEditedFind(null);
  };

  const handleSaved = () => {
    setToastMessage(intl.formatMessage(sitePasMessages.saved));
  };

  const handleCreateClose = () => {
    setCreateFindId(null);
  };

  const handleCreated = (amcrId: string) => {
    if (createFindId) {
      reconcileFindsInCache(client.cache, [createFindId]);
    }

    setToastMessage(intl.formatMessage(sitePasMessages.created, { amcrId }));
  };

  const handlePhotosClose = () => {
    setPhotosFind(null);
  };

  const handlePhotosUploaded = (count: number) => {
    if (photosFind) {
      reconcileFindsInCache(client.cache, [photosFind.id]);
    }

    setToastMessage(
      intl.formatMessage(sitePasMessages.photosAdded, { count })
    );
  };

  const handleSubmitClose = () => {
    setSubmitFindId(null);
  };

  const handleSubmitted = () => {
    setToastMessage(intl.formatMessage(sitePasMessages.submitted));
  };

  const handleToastClose = () => {
    setToastMessage(null);
  };

  if (!stage) {
    return <Navigate replace to={getStagePathname(siteId, 'unprocessed')} />;
  }

  return (
    <Stack gap={2}>
      <TabBar
        aria-label={intl.formatMessage(sitePasMessages.stages)}
        items={tabs}
        onChange={handleTabChange}
        value={stage}
      />
      <PasFindTable
        rows={findsByStage[stage]}
        loading={loading && !data}
        state={state}
        stage={stage}
        pasEnabled={pasEnabled}
        onEdit={handleEdit}
        onCreate={handleCreate}
        onAddPhotos={handleAddPhotos}
        onSubmit={handleSubmit}
      />
      {editedFind && (
        <FindPasEditDialog
          find={editedFind}
          onClose={handleEditClose}
          onSaved={handleSaved}
        />
      )}
      {createFindId && (
        <AmcrPasCreateDialog
          findId={createFindId}
          onClose={handleCreateClose}
          onCreated={handleCreated}
        />
      )}
      {photosFind && (
        <AmcrPasPhotoDialog
          findId={photosFind.id}
          pasFiles={photosFind.amcrData?.files ?? NO_PAS_FILES}
          onClose={handlePhotosClose}
          onUploaded={handlePhotosUploaded}
        />
      )}
      {submitFindId && (
        <AmcrPasSubmitDialog
          findId={submitFindId}
          onClose={handleSubmitClose}
          onSubmitted={handleSubmitted}
        />
      )}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={TOAST_DURATION_MS}
        onClose={handleToastClose}
      >
        <Alert severity="success" variant="filled" onClose={handleToastClose}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
});

SitePas.displayName = 'SitePas';

export { SitePas };
